import React, { useEffect, useState } from 'react';
import { staffClient } from 'clients/staff';
import Button from 'ui/Button';
import Input from 'ui/Input';
import Modal from 'ui/Modal';
import Table, { Column } from 'ui/Table';
import Loading from 'ui/Loading';
import Section from 'ui/Section';
import { Switch } from 'ui/shadcn';
import ImageUploader, { ImageFile } from 'ui/ImageUploader';
import { getS3UploadUrl, uploadS3File } from 'utils/s3';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface HistoryItem {
    year: string;
    desc: string;
    highlight?: boolean;
}

interface History {
    id: number;
    era: string;
    title: string;
    image_url?: string;
    sort_year: number;
    items: HistoryItem[];
    is_active: boolean;
    display_order: number;
}

const empty: Omit<History, 'id'> = {
    era: '',
    title: '',
    image_url: '',
    sort_year: new Date().getFullYear(),
    items: [],
    is_active: true,
    display_order: 0,
};

const HistoryManager: React.FC = () => {
    const [list, setList] = useState<History[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<History | null>(null);
    const [form, setForm] = useState<Omit<History, 'id'>>({ ...empty });
    const [eraImages, setEraImages] = useState<ImageFile[]>([]);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { fetchList(); }, []);

    const fetchList = async () => {
        try {
            const res = await staffClient.get('/website-company-history');
            setList(res.data);
        } catch {
            toast.error('연혁을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ ...empty });
        setEraImages([]);
        setShowModal(true);
    };

    const openEdit = (row: History) => {
        setEditing(row);
        setForm({
            era: row.era,
            title: row.title,
            image_url: row.image_url || '',
            sort_year: row.sort_year,
            items: row.items || [],
            is_active: row.is_active,
            display_order: row.display_order,
        });
        setEraImages(row.image_url ? [{ id: '1', url: row.image_url, name: 'era.jpg' }] : []);
        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            setUploading(true);
            let imageUrl = form.image_url || '';

            if (eraImages.length > 0 && eraImages[0].file) {
                const file = eraImages[0].file;
                const signedUrl = await getS3UploadUrl(file);
                if (signedUrl) {
                    const uploaded = await uploadS3File(file, signedUrl);
                    if (uploaded) imageUrl = uploaded;
                }
            } else if (eraImages.length === 0) {
                imageUrl = '';
            } else if (eraImages[0].url) {
                imageUrl = eraImages[0].url;
            }

            const payload = { ...form, image_url: imageUrl };

            if (editing) {
                await staffClient.patch(`/website-company-history/${editing.id}`, payload);
                toast.success('연혁이 수정되었습니다.');
            } else {
                await staffClient.post('/website-company-history', payload);
                toast.success('연혁이 추가되었습니다.');
            }
            setShowModal(false);
            fetchList();
        } catch (error) {
            console.error('History save failed:', error);
            toast.error('저장에 실패했습니다.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (row: History) => {
        if (!window.confirm(`"${row.era}" 연혁을 삭제하시겠습니까?`)) return;
        try {
            await staffClient.delete(`/website-company-history/${row.id}`);
            toast.success('삭제되었습니다.');
            fetchList();
        } catch {
            toast.error('삭제에 실패했습니다.');
        }
    };

    const handleToggle = async (row: History) => {
        try {
            const next = !row.is_active;
            await staffClient.patch(`/website-company-history/${row.id}`, { is_active: next });
            setList(prev => prev.map(r => r.id === row.id ? { ...r, is_active: next } : r));
            toast.success(next ? '활성화되었습니다.' : '비활성화되었습니다.');
        } catch {
            toast.error('상태 변경 실패');
        }
    };

    // Items 편집
    const addItem = () => setForm(f => ({ ...f, items: [...f.items, { year: '', desc: '', highlight: false }] }));
    const updateItem = (i: number, patch: Partial<HistoryItem>) =>
        setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
    const removeItem = (i: number) =>
        setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

    const columns: Column<History>[] = [
        { key: 'sort_year', header: '연도', width: '80px', align: 'center', render: (v) => <span>{v}</span> },
        { key: 'era', header: '연대', width: '120px', render: (v) => <span className="font-medium">{v}</span> },
        { key: 'title', header: '제목', render: (v) => <span>{v}</span> },
        {
            key: 'items',
            header: '항목수',
            width: '80px',
            align: 'center',
            render: (v: HistoryItem[]) => <span className="tabular-nums text-muted-foreground">{v?.length || 0}건</span>
        },
        {
            key: 'is_active',
            header: '노출',
            width: '70px',
            align: 'center',
            render: (value, row) => (
                <Switch
                    checked={!!value}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggle(row)}
                    aria-label="노출 여부"
                />
            ),
        },
    ];

    return (
        <div>
            <Section
                className="overflow-hidden"
                title={
                    <>
                        연혁
                        {!loading && (
                            <span className="text-xs font-normal text-muted-foreground tabular-nums">
                                총 <span className="font-medium text-foreground">{list.length}</span>건
                            </span>
                        )}
                    </>
                }
                description="홈페이지 회사소개 > 개요/연혁에 표시될 연혁을 관리합니다. 행을 클릭하면 수정할 수 있습니다."
                actions={<Button variant="primary" icon={<PlusIcon />} onClick={openCreate}>연혁 추가</Button>}
                flush
            >
                {loading ? (
                    <Loading text="연혁을 불러오는 중..." />
                ) : (
                    <Table
                        columns={columns}
                        data={list}
                        pagination
                        pageSize={20}
                        emptyMessage="등록된 연혁이 없습니다."
                        onRowClick={openEdit}
                        actionsColumnWidth="90px"
                        actions={(row) => (
                            <Button
                                size="small"
                                variant="danger-outline"
                                onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
                            >
                                삭제
                            </Button>
                        )}
                    />
                )}
            </Section>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editing ? '연혁 수정' : '연혁 추가'}
                size="large"
            >
                <div className="grid grid-cols-3 gap-3">
                    <div className="form-group">
                        <label>연대 *</label>
                        <Input
                            value={form.era}
                            onChange={(e) => setForm({ ...form, era: e.target.value })}
                            placeholder="예: 2020 —"
                        />
                    </div>
                    <div className="form-group">
                        <label>제목 *</label>
                        <Input
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="예: 도약과 혁신"
                        />
                    </div>
                    <div className="form-group">
                        <label>정렬 연도 *</label>
                        <Input
                            type="number"
                            value={form.sort_year.toString()}
                            onChange={(e) => setForm({ ...form, sort_year: parseInt(e.target.value) || 0 })}
                            placeholder="2020"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>대표 이미지</label>
                    <ImageUploader
                        images={eraImages}
                        onChange={setEraImages}
                        maxImages={1}
                        multiple={false}
                        accept="image/*"
                    />
                    <p className="text-xs text-neutral-400 mt-1">시대를 대표하는 이미지 1장</p>
                </div>

                <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-neutral-700">연혁 항목</label>
                        <Button variant="secondary" size="small" icon={<PlusIcon />} onClick={addItem}>항목 추가</Button>
                    </div>
                    <div className="space-y-2">
                        {form.items.map((item, i) => (
                            <div key={i} className="grid grid-cols-[80px_1fr_auto_auto] items-center gap-2">
                                <input
                                    className="h-8 w-full rounded border border-input bg-background px-2 text-base text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                                    value={item.year}
                                    onChange={(e) => updateItem(i, { year: e.target.value })}
                                    placeholder="연도"
                                />
                                <input
                                    className="h-8 w-full min-w-0 rounded border border-input bg-background px-2 text-base text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                                    value={item.desc}
                                    onChange={(e) => updateItem(i, { desc: e.target.value })}
                                    placeholder="내용"
                                />
                                <label className="flex items-center gap-1 text-xs text-neutral-700 whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        checked={item.highlight || false}
                                        onChange={(e) => updateItem(i, { highlight: e.target.checked })}
                                    />
                                    강조
                                </label>
                                <button
                                    onClick={() => removeItem(i)}
                                    className="rounded p-1.5 text-neutral-400 hover:bg-muted hover:text-destructive"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {form.items.length === 0 && (
                            <p className="text-xs text-neutral-400 py-3 text-center">항목이 없습니다. '항목 추가'를 눌러주세요.</p>
                        )}
                    </div>
                </div>

                <div className="modal-footer">
                    <Button variant="primary" onClick={handleSave} loading={uploading} disabled={!form.era || !form.title}>
                        {editing ? '수정' : '추가'}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={uploading}>취소</Button>
                </div>
            </Modal>
        </div>
    );
};

export default HistoryManager;
